// src/components/admin/OnboardingChecklist.jsx
// #Revised UI_v2 — A4: Onboarding setup checklist widget for new salon accounts
import React, { useState, useEffect } from 'react';
import { collection, query, where, getCountFromServer } from 'firebase/firestore';
import { db } from '../../utils/firebase';
import '../../styles/onboarding-checklist.css';

const OnboardingChecklist = ({ salonConfig, services, salonId, onNavigate, isModuleEnabled }) => {
  const [staffCount, setStaffCount] = useState(null);
  const [promotionCount, setPromotionCount] = useState(null);
  const [dismissed, setDismissed] = useState(() =>
    localStorage.getItem(`onboarding_dismissed_${salonId}`) === 'true'
  );

  // Fetch staff and promotion counts
  useEffect(() => {
    if (!salonId || dismissed) return;

    const staffQ = query(collection(db, 'staff'), where('salonId', '==', salonId));
    getCountFromServer(staffQ)
      .then(snap => setStaffCount(snap.data().count))
      .catch(() => setStaffCount(0));

    const promoQ = query(collection(db, 'promotions'), where('salonId', '==', salonId));
    getCountFromServer(promoQ)
      .then(snap => setPromotionCount(snap.data().count))
      .catch(() => setPromotionCount(0));
  }, [salonId, dismissed]);

  if (dismissed || staffCount === null || promotionCount === null) return null;

  // ── Step completion checks ──────────────────────────────────────
  const hasBranding = !!(
    salonConfig?.branding?.name &&
    salonConfig?.branding?.logo?.url &&
    !salonConfig.branding.logo.url.startsWith('data:image/svg') // not the default fallback
  );

  const hasServices = (services?.length ?? 0) > 0;
  const hasStaff    = staffCount > 0;
  const hasHours    = !!(salonConfig?.businessHours?.regularHours?.some(h => !h.isClosed));
  const hasPromo    = promotionCount > 0;
  const bookingOn   = isModuleEnabled ? isModuleEnabled('bookings') : false;

  const steps = [
    {
      key: 'branding',
      title: 'Set up your brand identity',
      desc: 'Logo, colours, and business name',
      done: hasBranding,
      moduleId: 'core',
      tabId: 'branding',
    },
    {
      key: 'services',
      title: 'Add your services & pricing',
      desc: 'Create your service catalogue',
      done: hasServices,
      moduleId: 'core',
      tabId: 'services',
    },
    {
      key: 'staff',
      title: 'Add your staff members',
      desc: staffCount > 0
        ? `${staffCount} staff profile${staffCount !== 1 ? 's' : ''} added`
        : 'Add at least one staff member',
      done: hasStaff,
      moduleId: 'core',
      tabId: 'staff',
    },
    {
      key: 'hours',
      title: 'Set working hours',
      desc: 'Define your opening hours and staff schedules',
      done: hasHours,
      moduleId: 'bookings',
      tabId: 'working-hours',
    },
    {
      key: 'promotion',
      title: 'Create your first promotion',
      desc: 'Attract new clients with a welcome offer',
      done: hasPromo,
      moduleId: 'promotions',
      tabId: 'promotions',
    },
    {
      key: 'booking',
      title: 'Enable online booking',
      desc: 'Go live and start accepting appointments',
      done: bookingOn,
      moduleId: 'bookings',
      tabId: 'booking-calendar',
    },
  ];

  const completedCount = steps.filter(s => s.done).length;
  const progressPct    = Math.round((completedCount / steps.length) * 100);

  // Hide once everything is done
  if (completedCount === steps.length) return null;

  const handleDismiss = () => {
    localStorage.setItem(`onboarding_dismissed_${salonId}`, 'true');
    setDismissed(true);
  };

  return (
    <div className="onboarding-checklist">
      {/* Header */}
      <div className="onboarding-header">
        <div className="onboarding-header-text">
          <h3 className="onboarding-title">Welcome to BespokeBeauty 👋</h3>
          <p className="onboarding-subtitle">
            Complete these 6 steps to get your salon live and accepting bookings.
          </p>
        </div>
        <button
          className="onboarding-dismiss"
          onClick={handleDismiss}
          title="Dismiss checklist"
          aria-label="Dismiss onboarding checklist"
        >
          ✕
        </button>
      </div>

      {/* Progress */}
      <div className="onboarding-progress-label">
        {completedCount} of {steps.length} steps complete
      </div>
      <div className="onboarding-progress-bar">
        <div className="onboarding-progress-fill" style={{ width: `${progressPct}%` }} />
      </div>

      {/* Steps */}
      <div className="onboarding-steps">
        {steps.map((step) => (
          <div
            key={step.key}
            className={`onboarding-step${step.done ? ' done' : ''}`}
            onClick={() => !step.done && onNavigate(step.moduleId, step.tabId)}
            role={step.done ? undefined : 'button'}
            tabIndex={step.done ? undefined : 0}
            onKeyDown={e => !step.done && e.key === 'Enter' && onNavigate(step.moduleId, step.tabId)}
          >
            <div className={`onboarding-check${step.done ? ' done' : ''}`} />
            <div className="onboarding-step-text">
              <strong>{step.title}</strong>
              <span>{step.desc}</span>
            </div>
            <span className="onboarding-arrow">{step.done ? '✓' : '→'}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default OnboardingChecklist;
